import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconsSend from '@mui/icons-material/Send';
import Button from '@mui/material/Button';
import { ApolloError } from '@apollo/client';
import useNotification from '../../../../components/_hook/useNotification';
import ButtonSubmit from '../../../../components/_button/ButtonSubmit';
import useForm from '../../../../components/_hook/useForm';
import { useCreatePlanAdministratorMutation } from '../../../../../graphql/mutation/plan/planAdministrator/createPlanAdministratorMutation';
import { IValue } from '../../../../components/_select/_module/Select';
import SelectSearchUser from '../../../../components/_select/SelectSearchUser';
import Context from '../../../../context';

interface IProps {
  onSubmit?: () => void;
  onCancel: () => void;
}

export default function FormCreatePlanAdministrator(props: IProps) {
  interface IFormValue {
    user: IValue | null;
  }

  const { t } = useTranslation();
  const { onSubmit, onCancel } = props;
  const { notification } = useNotification();
  const { company } = useContext(Context);

  function onCompleted() {
    notification.success(t('success'));
    if (onSubmit) {
      onSubmit();
    }
  }

  function onError(e: ApolloError) {
    notification.error(e.message);
  }

  const [
    createPlanAdministratorMutation,
    { loading: isCreatingPlanAdministrator },
  ] = useCreatePlanAdministratorMutation(
    `updatedPlan {
				_id
				users {
				  id
                  status
                  superAdmin
                  userData {
                    _id
                    profile {
                      name {
                        firstname
                        lastname
                      }
                    }
                  }
                }
			}`,
    {
      onCompleted,
      onError,
    }
  );

  async function onSubmitForm(tempValues: IFormValue) {
    if (tempValues.user) {
      await createPlanAdministratorMutation({
        variables: {
          companyId: company._id,
          userId: tempValues.user.value,
        },
      });
    }
  }

  const { values, setValue, handleSubmit, errors } = useForm<IFormValue>({
    defaultValues: {
      user: null,
    },
    onSubmit: onSubmitForm,
    onValidate: (tempValues) => {
      const newErrors: Record<string, string> = {};

      if (!tempValues.user) {
        newErrors.user = t('please_select__item__', {
          item: t('user'),
        });
      }

      return newErrors;
    },
  });

  return (
    <>
      <DialogTitle>
        {t('add')} {t('administrator')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SelectSearchUser<IValue>
              required
              fullWidth
              value={values.user}
              menuPosition="fixed"
              placeholder={t('email')}
              onChange={(option) => {
                setValue('user', option);
              }}
              useUserAvatar
              error={Boolean(errors.user)}
              helperText={errors.user}
              searchByField="emailAddress"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <Button variant="text" onClick={onCancel}>
              {t('cancel')}
            </Button>
          </Grid>
          <Grid item>
            <ButtonSubmit
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              isSubmitting={isCreatingPlanAdministrator}
              label={t('submit')}
              icon={<IconsSend />}
            />
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
}
